import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { TypeFilter, type TypeFilterProps } from '~/components/Filters/TypeFilter/TypeFilter';
import { useTypeFilterUpdate } from '~/components/Filters/TypeFilter/useTypeFilterUpdate';
import { type SingleFilterProps } from '~/components/Filters/types';
import { clearItemTypeFiltersAction } from '~/store/filters/actions';
import {
  hasItemTypesValuesSelector,
  selectedAssetFileKindFiltersSelector,
  selectedItemTypesFiltersSelector,
} from '~/store/filters/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

export type SingleTypeFilterProps = {
  availableTypes?: TypeFilterProps['availableTypes'];
} & SingleFilterProps;

export const SingleTypeFilter = memo(({ availableTypes, onApplyClick }: SingleTypeFilterProps) => {
  const {
    onTypeSelectedChange: onTypeSelectedChangeCallback,
    onAssetFileKindSelectedChange: onAssetFileKindSelectedChangeCallback,
  } = useTypeFilterUpdate();
  const dispatch = useDispatch();
  const hasValues = useAirSelector(hasItemTypesValuesSelector);
  const selectedTypes = useSelector(selectedItemTypesFiltersSelector);
  const selectedAssetFileKinds = useSelector(selectedAssetFileKindFiltersSelector);
  const clearValues = useCallback(() => {
    dispatch(clearItemTypeFiltersAction());
  }, [dispatch]);

  const onTypeSelectedChange = useCallback<TypeFilterProps['onTypeSelectedChange']>(
    (params) => {
      onTypeSelectedChangeCallback(params);
      onApplyClick();
    },
    [onApplyClick, onTypeSelectedChangeCallback],
  );

  const onAssetFileKindSelectedChange = useCallback<TypeFilterProps['onAssetFileKindSelectedChange']>(
    (params) => {
      onAssetFileKindSelectedChangeCallback(params);
      onApplyClick();
    },
    [onApplyClick, onAssetFileKindSelectedChangeCallback],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="type"
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <TypeFilter
        availableTypes={availableTypes}
        selectedTypes={selectedTypes}
        selectedAssetFileKinds={selectedAssetFileKinds}
        onTypeSelectedChange={onTypeSelectedChange}
        onAssetFileKindSelectedChange={onAssetFileKindSelectedChange}
      />
    </SingleFilterContainer>
  );
});

SingleTypeFilter.displayName = 'SingleTypeFilters';
